import type {
  ActOnApprovalOptionInput,
  ApprovalAction,
  ApprovalActionNoteOptions,
} from '@cims-online/shared'
import { useCallback, useRef, useState } from 'react'
import { createConfiguredFieldsSession } from '../../utils/configuredFields/utils'
import type {
  UseApprovalActionsOptions,
  ApprovalActionController,
  ApprovalActionSession,
  ApprovalActionTarget,
  ApprovalActionFormValues,
} from './types'

/** Authorization is resolved by the API before an action reaches this controller. */
function canSubmit(action: ApprovalAction): boolean {
  return action.authorized
}

/** Applies enabled note defaults in order without inheriting a prior decision's note. */
function createInitialNote(
  note: ApprovalActionNoteOptions | null
): string | undefined {
  if (!note) return

  let value = ''
  for (const transformer of note.initialValueTransformers)
    if (transformer.enabled) value = transformer.value
  return value
}

/** Captures opening defaults; the session number resets form edits when reopened. */
function createSession(
  target: ApprovalActionTarget,
  number: number
): ApprovalActionSession {
  const baseValues = target.baseValues ?? {}
  const initialValueContext = target.initialValueContext ?? {}

  return {
    fieldSession: createConfiguredFieldsSession({
      fields: target.action.fields,
      baseValues,
      approvalFields: target.approvalFields,
      initialValueContext,
    }),
    initialNote: createInitialNote(target.action.note),
    number,
    target,
  }
}

/**
 * Coordinates an approval cell's immediate submissions and drawer interactions.
 *
 * The hook deliberately accepts an injected submit callback instead of a
 * generated mutation hook. The Records page can therefore own its refetch and
 * notification sequence while the cell and overlays share one pending-action
 * guard.
 */
export function useApprovalActions({
  onSubmit,
  onSuccess,
}: UseApprovalActionsOptions): ApprovalActionController {
  const submitting = useRef(false)
  const lastSessionNumber = useRef(0)
  const [pendingAction, setPendingAction] =
    useState<ApprovalActionController['pendingAction']>(null)
  const [session, setSession] = useState<ApprovalActionSession | null>(null)
  const [open, setOpen] = useState(false)

  const submit = useCallback(
    async (
      target: ApprovalActionTarget,
      formValues: ApprovalActionFormValues
    ): Promise<boolean> => {
      if (submitting.current) return false

      submitting.current = true
      setPendingAction({
        action: target.action.action,
        optionId: target.optionId,
        recordId: target.recordId,
      })
      try {
        const input: ActOnApprovalOptionInput = {
          action: target.action.action,
          optionId: target.optionId,
          recordId: target.recordId,
          values: formValues.values,
        }
        if (target.action.note) input.note = formValues.note ?? null

        const result = await onSubmit(input)
        setOpen(false)
        try {
          await onSuccess?.(result, target)
        } catch {
          // A refresh failure must not turn a completed mutation back into a retryable form.
        }
        return true
      } catch {
        // The caller owns request-error presentation; preserve the form to retry.
        return false
      } finally {
        submitting.current = false
        setPendingAction(null)
      }
    },
    [onSubmit, onSuccess]
  )

  const onAction = useCallback(
    async (target: ApprovalActionTarget): Promise<void> => {
      if (submitting.current || open || !canSubmit(target.action)) return

      lastSessionNumber.current += 1
      const nextSession = createSession(target, lastSessionNumber.current)
      const hasInputs =
        target.action.fields.length > 0 || target.action.note !== null
      if (!hasInputs) {
        await submit(target, {
          note: undefined,
          values: {},
        })
        return
      }

      setSession(nextSession)
      setOpen(true)
    },
    [open, submit]
  )

  const close = useCallback(() => {
    if (!submitting.current) setOpen(false)
  }, [])

  const submitSession = useCallback(
    async (formValues: ApprovalActionFormValues): Promise<boolean> => {
      if (!session || !open) return false
      return submit(session.target, formValues)
    },
    [open, session, submit]
  )

  return {
    close,
    onAction,
    open,
    pending: pendingAction !== null,
    pendingAction,
    session,
    submitSession,
  }
}
